import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './AllFeaturedCoursesPage.css';
import './FeaturedTab.css';
import useCatalogManagementStore from '@/stores/AdminStore/CatalogManagementStore';
import { MessageModal } from '@/Components/common/MessageModal';
import ConfirmationModal from '@/Components/common/ConfirmationModal/ConfirmationModal';
import LoadingSpinner from '@/Components/common/LoadingSpinner';
import { ActiveCourse } from '@/services/PagesServices/CatalogManagementService';

const AllFeaturedCoursesPage: React.FC = () => {
  const navigate = useNavigate();
  const { featuredCourses, toggleFeaturedCourseStatus, activeCourses, fetchActiveCourses, createFeaturedCourse } = useCatalogManagementStore();

  const [isOperationLoading, setIsOperationLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [messageModal, setMessageModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'success',
  });

  // Add Featured Course Modal state
  const [featuredModalOpen, setFeaturedModalOpen] = useState(false);
  const [featuredFormData, setFeaturedFormData] = useState({
    courseId: '',
    courseInput: '',
  });
  const [showFeaturedCourseDropdown, setShowFeaturedCourseDropdown] = useState(false);
  const [filteredFeaturedCourses, setFilteredFeaturedCourses] = useState<ActiveCourse[]>([]);

  const showMessageModal = (title: string, message: string, type: 'success' | 'error') => {
    setMessageModal({ isOpen: true, title, message, type });
  };

  const closeMessageModal = () => {
    setMessageModal({ ...messageModal, isOpen: false });
  };

  // Featured Course Modal functions
  const openFeaturedModal = async () => {
    setFeaturedModalOpen(true);
    setFeaturedFormData({ courseId: '', courseInput: '' });
    // Fetch active courses when modal opens
    try {
      await fetchActiveCourses();
    } catch (error) {
      console.error('Failed to fetch active courses:', error);
    }
  };

  const closeFeaturedModal = () => {
    setFeaturedModalOpen(false);
    setFeaturedFormData({ courseId: '', courseInput: '' });
    setShowFeaturedCourseDropdown(false);
  };

  const handleFeaturedSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!featuredFormData.courseId) {
      showMessageModal('Error', 'Please select a course', 'error');
      return;
    }

    // Check if the course is already in the featured courses list
    const isAlreadyFeatured = featuredCourses?.some(
      course => course.id === featuredFormData.courseId
    );

    if (isAlreadyFeatured) {
      const courseName = featuredFormData.courseInput || 'This course';
      showMessageModal('Error', `${courseName} is already added as a featured course.`, 'error');
      return;
    }

    setIsOperationLoading(true);
    try {
      await createFeaturedCourse(parseInt(featuredFormData.courseId));
      closeFeaturedModal();
      showMessageModal('Success', 'Featured course added successfully!', 'success');
    } catch (error: any) {
      console.error('Failed to add featured course:', error);
      showMessageModal('Error', error.message || 'Failed to add featured course. Please try again.', 'error');
    } finally {
      setIsOperationLoading(false);
    }
  };

  const handleFeaturedCourseInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFeaturedFormData({ ...featuredFormData, courseInput: value });
    setShowFeaturedCourseDropdown(true);
  };

  const selectFeaturedCourse = (course: ActiveCourse) => {
    setFeaturedFormData({
      courseId: course.id,
      courseInput: course.title,
    });
    setShowFeaturedCourseDropdown(false);
  };

  const handleFeaturedCourseInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowFeaturedCourseDropdown(false);
    }
  };

  // Filter courses based on search input for featured courses
  useEffect(() => {
    if (activeCourses && featuredFormData.courseInput) {
      const filtered = activeCourses.filter(course =>
        course.title?.toLowerCase().includes(featuredFormData.courseInput.toLowerCase())
      );
      setFilteredFeaturedCourses(filtered);
    } else {
      setFilteredFeaturedCourses(activeCourses || []);
    }
  }, [activeCourses, featuredFormData.courseInput]);

  const toggleCourseActive = async (courseId: string) => {
    // Find the course to check its current status
    const course = featuredCourses?.find(c => c.id === courseId);
    if (!course) return;

    const wasActive = course.isActive;

    setIsOperationLoading(true);
    try {
      await toggleFeaturedCourseStatus(parseInt(courseId));
      const statusMessage = wasActive
        ? 'Featured course disabled successfully!'
        : 'Featured course enabled successfully!';
      showMessageModal('Success', statusMessage, 'success');
    } catch (error: any) {
      showMessageModal('Error', error.message || 'Failed to update course status', 'error');
    } finally {
      setIsOperationLoading(false);
    }
  };

  // Filter courses based on search term
  const filteredCourses = featuredCourses?.filter((course) => {
    const titleMatch = course.title.toLowerCase().includes(searchTerm.toLowerCase());
    const categoryMatch = course.category.toLowerCase().includes(searchTerm.toLowerCase());
    return titleMatch || categoryMatch;
  }) || [];

  // Pagination logic
  const totalCourses = filteredCourses.length;
  const totalPages = Math.ceil(totalCourses / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCourses = filteredCourses.slice(startIndex, endIndex);

  // Reset to page 1 when search term changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="all-featured-courses-page">
      <div className="all-featured-courses-header">
        <button
          className="all-featured-courses-back-button"
          onClick={() => navigate('/admin/content/catalog', { state: { activeTab: 'featured' } })}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>
        <div className="all-featured-courses-title-wrapper">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="all-featured-courses-icon">
            <path
              d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <h1 className="all-featured-courses-title">All Featured Courses</h1>
        </div>
        <div className="all-featured-courses-count">
          {featuredCourses?.length || 0} {featuredCourses?.length === 1 ? 'Course' : 'Courses'}
        </div>
        <button className="admin-catalog-add-featured-button" onClick={openFeaturedModal}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Add Featured Course
        </button>
      </div>

      {/* Search Bar */}
      {featuredCourses && featuredCourses.length > 0 && (
        <div className="all-featured-courses-search-wrapper">
          <div className="all-featured-courses-search-container">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="all-featured-courses-search-icon">
              <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
              <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              className="all-featured-courses-search-input"
              placeholder="Search by course title or category..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <button
                className="all-featured-courses-search-clear"
                onClick={() => {
                  setSearchTerm('');
                  setCurrentPage(1);
                }}
                aria-label="Clear search"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {!featuredCourses || featuredCourses.length === 0 ? (
        <div className="all-featured-courses-empty-state">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path
              d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <h3>No Featured Courses Yet</h3>
          <p>Featured courses will be displayed prominently on the learner catalog homepage. Add your first featured course to get started.</p>
        </div>
      ) : filteredCourses.length === 0 ? (
        <div className="all-featured-courses-empty-state">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path
              d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <h3>No Courses Found</h3>
          <p>No featured courses match your search criteria. Try adjusting your search.</p>
        </div>
      ) : (
        <>
          <div className="all-featured-courses-list">
            {paginatedCourses.map((course) => (
            <div key={course.id} className="all-featured-course-item">
              <div className="all-featured-course-drag-handle">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M5 9l7 7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M5 15l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>

              <div className="all-featured-course-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2V3zM22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7V3z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              <div className="all-featured-course-info">
                <h3 className="all-featured-course-title">{course.title}</h3>
                <div className="all-featured-course-meta">
                  <span className="all-featured-course-category">{course.category}</span>
                  <div className="all-featured-course-rating">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#FFA500">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                    <span>{course.rating}</span>
                  </div>
                  <span className="all-featured-course-enrolled">{course.enrolled} enrolled</span>
                </div>
              </div>

              <div className="all-featured-course-actions">
                <label className="all-featured-course-toggle-switch">
                  <input
                    type="checkbox"
                    checked={course.isActive}
                    onChange={() => toggleCourseActive(course.id)}
                  />
                  <span className="all-featured-course-toggle-slider"></span>
                </label>
              </div>
            </div>
          ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="all-featured-courses-pagination">
              <button
                className="all-featured-courses-pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Previous
              </button>

              <div className="all-featured-courses-pagination-pages">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Show first page, last page, current page, and pages around current
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        className={`all-featured-courses-pagination-page ${page === currentPage ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="all-featured-courses-pagination-ellipsis">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                className="all-featured-courses-pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          )}

          {/* Results Info */}
          <div className="all-featured-courses-results-info">
            Showing {startIndex + 1} - {Math.min(endIndex, totalCourses)} of {totalCourses} {totalCourses === 1 ? 'course' : 'courses'}
          </div>
        </>
      )}

      {/* Add Featured Course Modal */}
      {featuredModalOpen && ReactDOM.createPortal(
        <div className="admin-catalog-modal-overlay" onClick={closeFeaturedModal}>
          <div className="admin-catalog-modal-content admin-catalog-featured-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-catalog-modal-header">
              <div className="admin-catalog-modal-header-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 className="admin-catalog-modal-title">Add Featured Course</h2>
              <button className="admin-catalog-modal-close" onClick={closeFeaturedModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleFeaturedSubmit}>
              <div className="admin-catalog-form-group">
                <label htmlFor="featuredCourse" className="admin-catalog-form-label">
                  Select Course <span className="admin-catalog-required">*</span>
                </label>
                <div className="admin-catalog-course-input-wrapper" style={{ position: 'relative' }}>
                  <input
                    type="text"
                    id="featuredCourse"
                    className="admin-catalog-form-input"
                    placeholder="Search for courses..."
                    value={featuredFormData.courseInput}
                    onChange={handleFeaturedCourseInputChange}
                    onKeyDown={handleFeaturedCourseInputKeyPress}
                    onFocus={() => setShowFeaturedCourseDropdown(true)}
                    required
                  />
                  <button
                    type="button"
                    className="admin-catalog-dropdown-toggle-btn"
                    onClick={() => setShowFeaturedCourseDropdown(!showFeaturedCourseDropdown)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>

                  {/* Dropdown */}
                  {showFeaturedCourseDropdown && filteredFeaturedCourses.length > 0 && (
                    <div
                      className="admin-catalog-course-dropdown"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        marginTop: '4px',
                        zIndex: 1000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    >
                      {filteredFeaturedCourses.map((course) => (
                        <div
                          key={course.id}
                          className="admin-catalog-course-dropdown-item"
                          onClick={() => selectFeaturedCourse(course)}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer'
                          }}
                        >
                          <div style={{ fontWeight: 500, fontSize: '14px' }}>
                            {course.title}
                          </div>
                          {course.description && (
                            <div style={{ fontSize: '12px', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {course.description}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="admin-catalog-info-box">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 8h.01M12 12v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <div>
                  <strong>Featured Course Settings</strong>
                  <p>Featured courses will be displayed prominently on the learner catalog homepage. Use the order controls to arrange them.</p>
                </div>
              </div>

              <div className="admin-catalog-modal-actions">
                <button type="button" className="admin-catalog-btn-cancel" onClick={closeFeaturedModal}>
                  Cancel
                </button>
                <button type="submit" className="admin-catalog-btn-save">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17 21v-8H7v8M7 3v5h8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Add Course
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Message Modal for Success/Error */}
      {messageModal.isOpen && ReactDOM.createPortal(
        <MessageModal
          isOpen={messageModal.isOpen}
          onClose={closeMessageModal}
          type={messageModal.type}
          title={messageModal.title}
          message={messageModal.message}
        />,
        document.body
      )}

      {/* Full Screen Loading Spinner */}
      {isOperationLoading && ReactDOM.createPortal(
        <LoadingSpinner fullScreen message="Processing..." />,
        document.body
      )}
    </div>
  );
};

export default AllFeaturedCoursesPage;
